import { Link } from "react-router-dom";
import { ArrowLeft, Envelope, Question, ShieldCheck, Trash } from "@phosphor-icons/react";

export default function Support() {
  return (
    <div className="min-h-screen bg-bone-100" data-testid="support-page">
      <header
        className="bg-bone-100/75 backdrop-blur-xl border-b border-bone-300 sticky top-0 z-10"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand inline-block"></span> Hush
          </Link>
          <Link
            to="/"
            className="text-sm text-ink-500 hover:text-ink-900 flex items-center gap-2"
            data-testid="support-back"
          >
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12 prose-content">
        <p className="eyebrow">Hush · Help & Support</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-3 mb-4">
          How can we help?
        </h1>
        <p className="text-ink-500 text-lg mb-12">
          Real humans, real answers. Most messages get a reply within 24 hours.
        </p>

        <a
          href="mailto:help@apphush.online?subject=Hush%20Support"
          className="block bg-white border border-bone-300 rounded-2xl p-6 mb-6 hover:border-brand transition-colors"
          data-testid="support-email-cta"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center flex-shrink-0">
              <Envelope size={22} className="text-brand" />
            </div>
            <div>
              <h3 className="font-serif text-xl mb-1">Email us</h3>
              <p className="text-ink-500">
                <span className="text-ink-900">help@apphush.online</span> · Replies within 24h.
              </p>
            </div>
          </div>
        </a>

        <h2 className="font-serif text-2xl mt-12 mb-6 flex items-center gap-3">
          <Question size={24} className="text-brand" /> Frequently asked
        </h2>

        <Faq
          q="How do I record my first journal entry?"
          a="Sign in, then tap the large pink record button on the dashboard. Speak for up to 5 minutes. Hush will transcribe your voice and tag the mood automatically once you tap stop."
        />
        <Faq
          q="Where is my audio stored?"
          a="It isn't. Audio is sent to OpenAI Whisper for transcription, then immediately discarded. Only the transcript text, mood label, and short emotion tags remain on our servers."
        />
        <Faq
          q="How do I cancel my Hush Plus subscription?"
          a="On iOS: open Settings → tap your Apple ID at the top → Subscriptions → Hush → Cancel. On Android: open Google Play → menu → Subscriptions → Hush → Cancel. We don't store payment details — Apple and Google handle all billing."
        />
        <Faq
          q="Will Hush show me ads or sell my data?"
          a="No. Hush has zero ads, zero trackers, and we never sell or share your data with third parties. Your journal is yours."
        />
        <Faq
          q="My streak reset — can I get it back?"
          a="Streaks reset when you miss a day, by design. We don't want streaks to become a source of shame. Just open Hush today and start a new one."
        />
        <Faq
          q="How do I change my reminder time?"
          a="On the dashboard, tap the gear icon (top-right) → Notifications → pick your time → Save. You can disable reminders entirely from the same place."
        />
        <Faq
          q="How do I delete my account?"
          a={
            <>
              In the app: gear icon → Account → <strong>Delete my account</strong> (red button at the bottom). Or visit{" "}
              <Link to="/delete-account" className="underline text-brand">
                hush.app/delete-account
              </Link>
              . All journals, audio, and account data are permanently removed within 30 seconds.
            </>
          }
        />
        <Faq
          q="I forgot my password."
          a={
            <>
              On the sign-in screen tap <strong>"Forgot password?"</strong> — we'll email a reset link.
              Or visit{" "}
              <Link to="/forgot-password" className="underline text-brand">
                hush.app/forgot-password
              </Link>
              .
            </>
          }
        />
        <Faq
          q="The app is blank / not responding."
          a="Force-quit the app and reopen. If the issue persists, email us — we usually push a fix within 48 hours."
        />

        <div className="grid sm:grid-cols-3 gap-4 mt-16">
          <Link
            to="/privacy"
            className="block bg-white border border-bone-300 rounded-2xl p-5 hover:border-brand transition-colors"
            data-testid="support-link-privacy"
          >
            <ShieldCheck size={22} className="text-brand mb-3" />
            <div className="font-serif text-lg">Privacy Policy</div>
            <p className="text-sm text-ink-500 mt-1">How we handle your data.</p>
          </Link>
          <Link
            to="/terms"
            className="block bg-white border border-bone-300 rounded-2xl p-5 hover:border-brand transition-colors"
            data-testid="support-link-terms"
          >
            <Question size={22} className="text-brand mb-3" />
            <div className="font-serif text-lg">Terms of Service</div>
            <p className="text-sm text-ink-500 mt-1">The rules of using Hush.</p>
          </Link>
          <Link
            to="/delete-account"
            className="block bg-white border border-bone-300 rounded-2xl p-5 hover:border-brand transition-colors"
            data-testid="support-link-delete"
          >
            <Trash size={22} className="text-brand mb-3" />
            <div className="font-serif text-lg">Delete account</div>
            <p className="text-sm text-ink-500 mt-1">Erase everything in one tap.</p>
          </Link>
        </div>

        <p className="text-sm text-ink-500 mt-16 pb-12">
          Hush is built by <span className="text-ink-900">Topline Design (Pty) Ltd</span>. We're a tiny team
          and we read every message. Thank you for using Hush.
        </p>
      </main>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <details className="bg-white border border-bone-300 rounded-xl p-5 mb-3 group">
      <summary className="cursor-pointer font-serif text-lg text-ink-900 flex items-center justify-between list-none">
        <span>{q}</span>
        <span className="text-ink-500 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
      </summary>
      <div className="mt-3 text-ink-500 leading-relaxed">{a}</div>
    </details>
  );
}
